import { useState, useEffect, useRef } from "react";
import "./App.css";
import { supabase } from "./lib/supabase";
import { linkOneSignalUser as linkOneSignal, logoutOneSignalUser, processPendingExternalUserId, registerPlayerWithBackend } from "./lib/onesignal";
import { clearAllPcosLocalStorage, clearAllCaches, isOnboardingComplete, setOnboardingComplete, getVerifiedUserId } from "./lib/userDataService";
import SplashScreen from "./components/SplashScreen";
import AuthScreen from "./components/AuthScreen";
import ForgotPasswordScreen from "./components/ForgotPasswordScreen";
import ResetPasswordScreen from "./components/ResetPasswordScreen";
import OnboardingScreen from "./components/OnboardingScreen";
import HomeScreen from "./components/HomeScreen";
import CycleScreen from "./components/CycleScreen";
import HormonesScreen from "./components/HormonesScreen";
import SymptomsScreen from "./components/SymptomsScreen";
import InsightsScreen from "./components/InsightsScreen";
import TheoScreen from "./components/TheoScreen";
import CareHubScreen from "./components/CareHubScreen";
import CommunityScreen from "./components/CommunityScreen";
import ProfileScreen from "./components/ProfileScreen";
import BottomNav from "./components/BottomNav";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Global App Version
export const APP_VERSION = "1.0.4";

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState("home");
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [userName, setUserName] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  
  // Track the current user to detect user switching
  const currentUserIdRef = useRef(null);
  // Track if handleUserLogin has already initialized for this user (prevents double-fetch on boot)
  const initializedUserIdRef = useRef(null);
  
  // Auth sub-screen: 'login' | 'forgot' | 'reset'
  const [authScreen, setAuthScreen] = useState(() => {
    // Detect /reset-password URL on mount (user came from email link)
    if (window.location.pathname === '/reset-password') return 'reset';
    return 'login';
  });

  useEffect(() => {
    // Safety cap: hide splash after 4s no matter what (network failure / auth hang).
    // The data-driven effect below will normally hide it earlier.
    const safetyTimer = setTimeout(() => setShowSplash(false), 4000);

    // Version check
    const storedVersion = localStorage.getItem("app_version");
    if (storedVersion && storedVersion !== APP_VERSION) {
      setShowUpdateBanner(true);
    } else if (!storedVersion) {
      // First time user, save current version
      localStorage.setItem("app_version", APP_VERSION);
    }

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
      if (s?.user?.id) {
        handleUserLogin(s.user);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      // Handle PASSWORD_RECOVERY event — show reset screen, don't treat as normal login
      if (_event === 'PASSWORD_RECOVERY') {
        console.log('[App] PASSWORD_RECOVERY event — showing reset screen');
        setSession(s);
        setAuthScreen('reset');
        setLoading(false);
        return;
      }
      
      setSession(s);
      setLoading(false);
      if (s?.user?.id) {
        handleUserLogin(s.user);
      } else {
        // User logged out
        handleUserLogout();
      }
    });

    return () => {
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  // Data-driven splash hide: as soon as auth is resolved AND
  // (no session OR onboarding check finished), hide the splash early.
  // This prevents the hardcoded 2s wait when boot is actually faster,
  // and avoids a second splash phase between "showSplash off" and
  // "onboardingChecked true" on slower networks.
  useEffect(() => {
    if (loading) return;
    if (!session) {
      setShowSplash(false);
      return;
    }
    if (onboardingChecked) {
      setShowSplash(false);
    }
  }, [loading, session, onboardingChecked]);

  // Handle user login — verify auth, THEN fetch data
  const handleUserLogin = async (user) => {
    // ── 1. Verify auth is ready via getUser() ──
    const verifiedId = await getVerifiedUserId();
    if (!verifiedId) {
      console.log('[App] Auth verification failed — skipping data load');
      return;
    }

    // ── Dedupe: getSession().then() and onAuthStateChange(INITIAL_SESSION)
    //    both fire on cold boot. Skip the second pass for the same user
    //    to avoid duplicate network calls and re-renders that cause flicker. ──
    if (initializedUserIdRef.current === verifiedId) {
      console.log('[App] handleUserLogin already initialized for', verifiedId, '— skipping duplicate');
      return;
    }
    initializedUserIdRef.current = verifiedId;

    console.log('[App] AUTH VERIFIED — USER ID:', verifiedId);
    
    const previousUserId = currentUserIdRef.current;
    
    // Detect user switch on same device
    if (previousUserId && previousUserId !== verifiedId) {
      console.log('[App] User switched from', previousUserId, 'to', verifiedId);
      clearAllPcosLocalStorage();
      clearAllCaches();
    }
    
    // Update current user reference
    currentUserIdRef.current = verifiedId;
    
    // ── 2. Now fetch data (auth token is guaranteed valid) ──
    fetchUserName(verifiedId);
    await checkOnboardingStatus(verifiedId);
    
    // Link OneSignal with user's Supabase ID (not email)
    linkOneSignalWithUser(verifiedId, user.email);
    
    // Register OneSignal player ID with backend for push notifications
    registerPlayerWithBackend(verifiedId, user.email);
  };

  // Handle user logout — clear ALL state (local + in-memory)
  const handleUserLogout = () => {
    console.log('[App] User logged out, clearing ALL data');
    
    // Clear the current user reference
    currentUserIdRef.current = null;
    initializedUserIdRef.current = null;
    
    // Clear localStorage cache
    clearAllPcosLocalStorage();
    
    // Clear in-memory caches (profile cache, name cache, etc.)
    clearAllCaches();
    
    // Reset UI state
    setOnboardingChecked(false);
    setShowOnboarding(false);
    setUserName("");
    setCurrentTab("home");
    
    // Logout from OneSignal
    handleOneSignalLogout();
  };

  // Check if user has completed onboarding (user-specific) - CHECKS SUPABASE
  const checkOnboardingStatus = async (userId) => {
    console.log('[App] Checking onboarding status for user:', userId);
    // Pass skipVerify=true since handleUserLogin already verified auth right before this call.
    // Avoids a redundant getUser() round-trip during boot.
    const complete = await isOnboardingComplete(userId, { skipVerify: true });
    console.log('[App] Onboarding complete:', complete);
    if (!complete) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
    setOnboardingChecked(true);
  };

  // Handle onboarding completion
  const handleOnboardingComplete = (data) => {
    setShowOnboarding(false);
    // Optionally refresh user data if name was set during onboarding
    if (session?.user?.id) {
      fetchUserName(session.user.id);
    }
  };

  // Link OneSignal subscription with user's email
  // Link OneSignal with Supabase user.id (not email)
  const linkOneSignalWithUser = async (userId, userEmail) => {
    if (!userId) {
      console.log('[App] No userId provided for OneSignal linking');
      return;
    }
    
    console.log('[App] Linking OneSignal user — userId:', userId, '| email:', userEmail);
    
    try {
      const success = await linkOneSignal(userId, userEmail);
      if (success) {
        console.log('[App] OneSignal user linked — external_user_id:', userId);
      } else {
        console.log('[App] OneSignal user linking pending (permission may be needed)');
      }
    } catch (error) {
      console.error('[App] Error linking OneSignal user:', error);
    }
  };

  // Handle OneSignal logout when user logs out of the app
  const handleOneSignalLogout = async () => {
    console.log('[App] Logging out OneSignal user');
    try {
      await logoutOneSignalUser();
    } catch (error) {
      console.error('[App] Error logging out OneSignal user:', error);
    }
  };

  // Fetch user name from profiles table
  const fetchUserName = async (userId) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", userId)
        .single();
      
      if (!error && data?.name) {
        setUserName(data.name);
      }
    } catch (err) {
      console.error("Error fetching user name:", err);
    }
  };

  // The waiting service-worker registration (captured from the
  // `sw-update-ready` event in index.js). When set, clicking the banner's
  // "Update Now" tells this SW to take over, which fires controllerchange
  // and reloads the page with the new build.
  const pendingSwRegRef = useRef(null);

  // Listen for new service-worker activations dispatched from index.js
  useEffect(() => {
    const onReady = (e) => {
      console.log('[App] sw-update-ready event received');
      pendingSwRegRef.current = e?.detail?.registration || null;
      setShowUpdateBanner(true);
    };
    window.addEventListener('sw-update-ready', onReady);
    return () => window.removeEventListener('sw-update-ready', onReady);
  }, []);

  // Handle app update — primary path is to message the WAITING SW
  // (registered by index.js). controllerchange in index.js then reloads.
  // Falls back to a hard cache + SW wipe if no waiting SW is present
  // (e.g. user triggered via the APP_VERSION mismatch path).
  const handleUpdate = async () => {
    setIsUpdating(true);

    try {
      const reg = pendingSwRegRef.current;
      if (reg?.waiting) {
        console.log('[App] Activating waiting SW…');
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        // index.js will catch the controllerchange and reload. As a safety
        // net, force a reload after 4s if controllerchange never fires.
        setTimeout(() => {
          console.log('[App] Safety reload after SKIP_WAITING');
          window.location.reload();
        }, 4000);
        return;
      }

      // Fallback: clear caches + unregister all SWs + hard reload.
      console.log('[App] No waiting SW — performing full cache wipe');
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }

      // Clear localStorage except essential auth/session data
      const keysToPreserve = ['supabase.auth.token', 'sb-', 'supabase'];
      Object.keys(localStorage).forEach(key => {
        const shouldPreserve = keysToPreserve.some(preserve => key.includes(preserve));
        if (!shouldPreserve) localStorage.removeItem(key);
      });

      localStorage.setItem("app_version", APP_VERSION);
      window.location.reload(true);
    } catch (error) {
      console.error("Update failed:", error);
      setIsUpdating(false);
    }
  };

  // Dismiss update banner (user can update later)
  const dismissUpdate = () => {
    setShowUpdateBanner(false);
  };

  const handleSignOut = async () => {
    // signOut triggers onAuthStateChange → handleUserLogout handles cleanup
    await supabase.auth.signOut();
    // Show splash briefly while auth state settles
    setShowSplash(true);
    setTimeout(() => setShowSplash(false), 2000);
  };

  const handleTabChange = (tab) => {
    // Tapping the Community tab while already in Community pops back to the feed.
    if (tab === 'community' && currentTab === 'community' && typeof window.__resetCommunity === 'function') {
      window.__resetCommunity();
    }
    setCurrentTab(tab);
    window.scrollTo(0, 0);
  };

  if (showSplash || loading) return <SplashScreen />;
  
  // Show reset password screen when user arrives from email link
  if (authScreen === 'reset') {
    return <ResetPasswordScreen onComplete={() => {
      setAuthScreen('login');
      // Clean URL
      window.history.replaceState(null, '', '/');
      // Sign out so they re-authenticate with new password
      supabase.auth.signOut();
    }} />;
  }
  
  if (!session) {
    if (authScreen === 'forgot') {
      return <ForgotPasswordScreen onBack={() => setAuthScreen('login')} />;
    }
    return <AuthScreen onForgotPassword={() => setAuthScreen('forgot')} />;
  }
  
  // Wait until onboarding check finishes before showing anything
  // This prevents the main app from rendering with empty state
  if (!onboardingChecked) return <SplashScreen />;
  
  // Show onboarding for new users (after auth, before main app)
  if (showOnboarding) {
    return <OnboardingScreen userId={session.user.id} onComplete={handleOnboardingComplete} />;
  }

  const userId = session.user.id;
  const userEmail = session.user.email;

  const renderTab = () => {
    switch (currentTab) {
      case "home":
        return <HomeScreen userId={userId} userName={userName} onNavigate={handleTabChange} />;
      case "cycle":
        return <CycleScreen userId={userId} />;
      case "theo":
        return <TheoScreen userId={userId} userName={userName} onNavigate={handleTabChange} />;
      case "health":
        // Legacy tab id — redirect to the new Care hub which now
        // hosts Hormones / Symptoms / Insights / Wellness.
        return <CareHubScreen userId={userId} />;
      // Legacy aliases — keep so any direct setCurrentTab("hormones"|"symptoms"|"insights")
      // call from other screens (e.g. Theo "Update my data" button) still works by
      // landing on the correct sub-tab inside the Care hub.
      case "hormones":
        return <CareHubScreen userId={userId} initialSection="hormones" />;
      case "symptoms":
        return <CareHubScreen userId={userId} initialSection="symptoms" />;
      case "insights":
        return <CareHubScreen userId={userId} initialSection="insights" />;
      case "community":
        return <CommunityScreen userId={userId} onNavigate={handleTabChange} />;
      case "care":
        return <CareHubScreen userId={userId} initialSection="wellness" />;
      case "profile":
        return (
          <ProfileScreen
            userId={userId}
            userEmail={userEmail}
            onSignOut={handleSignOut}
          />
        );
      default:
        return <HomeScreen userId={userId} userName={userName} onNavigate={handleTabChange} />;
    }
  };

  return (
    <div className="app-shell">
      {/* Update Banner */}
      {showUpdateBanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #6D5EF5, #A78BFA)',
          padding: '12px 16px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(109,94,245,0.3)'
        }} data-testid="update-banner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>
              A new version of PCOS Insights is available
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={dismissUpdate}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: 6,
                padding: '6px 12px',
                color: '#fff',
                fontSize: 12,
                cursor: 'pointer'
              }}
              data-testid="dismiss-update-btn"
            >
              Later
            </button>
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              style={{
                background: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 14px',
                color: '#6D5EF5',
                fontSize: 12,
                fontWeight: 600,
                cursor: isUpdating ? 'wait' : 'pointer',
                opacity: isUpdating ? 0.7 : 1
              }}
              data-testid="update-now-btn"
            >
              {isUpdating ? 'Updating...' : 'Update Now'}
            </button>
          </div>
        </div>
      )}
      
      <header className="app-header" data-testid="app-header" style={{ top: showUpdateBanner ? 52 : 0 }}>
        <div className="header-logo">
          <img 
            src="/app_icon_48.png" 
            alt="PCOS" 
            style={{ width: 28, height: 28, objectFit: 'contain' }}
          />
          <span className="gradient-text header-brand">PCOS Insights</span>
        </div>
        
        {/* Profile Icon Button */}
        <button
          onClick={() => handleTabChange("profile")}
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: currentTab === 'profile' ? 'linear-gradient(135deg, #A78BFA, #6D5EF5)' : '#EEEAF8',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            marginRight: 16,
            transition: 'all 0.2s ease',
            boxShadow: currentTab === 'profile' ? '0 2px 8px rgba(109,94,245,0.3)' : 'none'
          }}
          data-testid="header-profile-btn"
        >
          {userName ? (
            <span style={{
              fontSize: 15,
              fontWeight: 700,
              color: currentTab === 'profile' ? '#fff' : '#6D5EF5',
              textTransform: 'uppercase'
            }}>
              {userName.charAt(0)}
            </span>
          ) : (
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke={currentTab === 'profile' ? '#fff' : '#6D5EF5'}
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </button>
      </header>
      <main className="app-content" style={{ marginTop: showUpdateBanner ? 52 : 0 }}>{renderTab()}</main>
      <BottomNav currentTab={currentTab} onTabChange={handleTabChange} />
      {currentTab === "home" && <PWAInstallPrompt />}
    </div>
  );
}

export default App;
